// Le bilan — la synthèse, puis la clôture. Dans cet ordre : tu vois ce que tu
// fermes avant de le fermer (§6.3). FlashCP ne clôture jamais d'office (P9).
//
// Le bilan reste consultable après clôture, en lecture seule, sans bouton.
//
// TOUT écart affiché ici passe par @/domain/ecart : signe explicite, et une
// couleur qui ne dépend JAMAIS du signe. Un excédent de caisse de 50 € est la
// même anomalie qu'un manque de 50 € — le vert ne dit qu'une chose, « aucun
// écart », et il ne se pose sur rien d'autre.

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { formatDate, formatMedium } from 'sotcp-core/dayUtils'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { progression, restantes } from '@/domain/parcours'
import {
  etatCoffre, etatDepouillement, etatPad, etatPrelevements, etatTickets,
} from '@/domain/derive'
import {
  couleurEcart, couleurEcartUnites, fmtEcart, fmtEcartUnites, lampeEcart, motEcart, severite,
} from '@/domain/ecart'
import { duree, fmtEur, heure, pluriel } from '@/lib/format'
import { messageErreur } from '@/lib/errors'
import { aller } from '@/lib/router'
import { useAuth } from '@/lib/auth'
import { Card, Chantier, Chip, Lamp, Modale, Notice, Vide, type Couleur } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import type { Kind } from '@/domain/derive'
import type { SessionHw } from '@/domain/types'

/** Une lampe de synthèse prend la PIRE de ses lignes, jamais leur moyenne. */
const RANG: Record<Couleur, number> = { ok: 0, act: 0, hw: 0, neutre: 1, warn: 2, stop: 3 }
const pire = (...lampes: Couleur[]): Couleur =>
  lampes.reduce<Couleur>((a, b) => (RANG[b] > RANG[a] ? b : a), 'ok')

const LIBELLE_TICKETS: Record<Kind, string> = {
  retours: 'Retours', formation: 'Formation', annules: 'Annulés',
}

/**
 * La lampe d'un écart en UNITÉS — même échelle que `couleurEcartUnites`, que
 * `@/domain/ecart` n'expose pas encore en `Couleur` (il lui manque un
 * `lampeEcartUnites` : quatre écrans redérivent aujourd'hui cette conversion).
 * Elle vivait ici en double, recopiée dans la lampe des écarts et dans les
 * réserves de clôture — deux occasions de diverger.
 */
const lampeUnites = (n: number): Couleur => {
  const a = Math.abs(Number(n) || 0)
  return a === 0 ? 'ok' : a === 1 ? 'warn' : 'stop'
}

export default function Bilan() {
  const run = useRun()
  const toast = useToast()
  const { nom } = useAuth()
  const [confirmation, setConfirmation] = useState(false)
  const [reouverture, setReouverture] = useState(false)
  const [sessions, setSessions] = useState<SessionHw[]>([])
  /** Clé de l'action serveur en cours — elle rend son bouton inerte. */
  const [travail, setTravail] = useState<string | null>(null)
  const { date, etapes, journee, consignes } = run

  // Un chargement qui revient après le démontage écrivait dans un composant
  // disparu : on change de journée, la réponse de l'ancienne arrive ensuite.
  useEffect(() => {
    let vivant = true
    api.chargerSessionsHw(date)
      .then((s) => { if (vivant) setSessions(s) })
      .catch(() => {})
    return () => { vivant = false }
  }, [date])

  const rapport = journee?.rapport ?? null
  const p = progression(etapes)
  const reste = restantes(etapes)
  const cloturee = run.run?.statut === 'cloturee'

  const pad = useMemo(() => etatPad(rapport), [rapport])
  const coffre = useMemo(() => etatCoffre(rapport), [rapport])
  const dep = useMemo(() => etatDepouillement(rapport), [rapport])
  const prelev = useMemo(() => etatPrelevements(rapport), [rapport])
  const tickets = useMemo(() => ({
    retours: etatTickets(rapport, 'retours'),
    formation: etatTickets(rapport, 'formation'),
    annules: etatTickets(rapport, 'annules'),
  }), [rapport])

  /**
   * Les écarts de caisses ne s'additionnent PAS en un seul chiffre.
   *
   * Le cumul signé de +50 € sur une caisse et de −50 € sur une autre vaut zéro :
   * le bilan annonçait « aucun écart » — en vert — sur deux anomalies à 50 €.
   * On garde donc trois lectures : le NET (ce que la journée doit expliquer au
   * comptable), le CUMUL en valeur absolue (ce qui a réellement dérapé) et le
   * nombre de caisses concernées. C'est la pire des caisses qui allume la lampe.
   */
  const caisses = useMemo(() => {
    const ops = Array.isArray(rapport?.rapportOps)
      ? (rapport.rapportOps as Array<Record<string, unknown>>) : []
    const diffs = ops.map((o) => Number(o.diff) || 0)
    const enEcart = diffs.filter((d) => severite(d) !== 'nul')
    return {
      compte: ops.length,
      net: +diffs.reduce((s, d) => s + d, 0).toFixed(2),
      cumul: +enEcart.reduce((s, d) => s + Math.abs(d), 0).toFixed(2),
      nb: enEcart.length,
      pire: enEcart.reduce((m, d) => (Math.abs(d) > Math.abs(m) ? d : m), 0),
    }
  }, [rapport])

  const consignesOuvertes = consignes.filter((c) => c.statut === 'a_faire')
  const coffreSansMotif = coffre.existe
    && severite(coffre.ecart) !== 'nul' && !coffre.motifEcart.trim()

  // Le cumul en valeur absolue est toujours au moins aussi grave que la pire
  // caisse : c'est lui qui gouverne la lampe, jamais le net qui se compense.
  //
  // Les PAD en attente comptent aussi : la carte les AFFICHE, et une lampe de
  // synthèse verte au-dessus d'une ligne ambre est un vert volé de plus. Ils
  // valent « warn » quel qu'en soit le nombre — c'est une file qui traîne, pas
  // un écart de mesure, et les deux autres endroits du bilan les cotent déjà
  // ainsi (les réserves de clôture et la carte du briefing).
  const lampeEcarts = pire(
    caisses.compte > 0 ? lampeEcart(caisses.cumul) : 'ok',
    coffre.existe ? lampeEcart(coffre.ecart) : 'ok',
    lampeEcart(pad.ecartTotal),
    prelev.compte ? lampeUnites(prelev.ecart) : 'ok',
    coffreSansMotif ? 'stop' : 'ok',
    run.padAttente.length > 0 ? 'warn' : 'ok',
  )

  /**
   * La carte des tickets porte des anomalies mais n'avait pas de lampe : il
   * fallait la lire en entier pour savoir s'il y avait à lire. Une nature non
   * importée reste NEUTRE — on ne sait pas, ce n'est pas « rien à signaler ».
   */
  const lampeTickets = pire(...(['retours', 'formation', 'annules'] as const)
    .flatMap<Couleur>((k) => {
      const t = tickets[k]
      return t.total === 0
        ? ['neutre']
        : [lampeUnites(t.jamaisVus), lampeUnites(t.manquants + t.anomalies)]
    }))

  /**
   * Ce que la clôture emporte avec elle. La journée se ferme quand même — P9,
   * FlashCP ne décide pas à ta place — mais elle ne se ferme pas en aveugle.
   */
  const reserves = useMemo(() => {
    const l: Array<{ c: Couleur; t: string; s: string }> = []
    for (const e of reste) {
      l.push(e.statut === 'de_cote'
        ? { c: 'warn', t: `${e.code} · ${e.libelle} — mise de côté`,
            s: `MOTIF : ${(e.motif ?? '—').toUpperCase()}` }
        : { c: 'stop', t: `${e.code} · ${e.libelle} — jamais soldée`,
            s: 'ELLE REVIENDRA DEMAIN' })
    }
    if (consignesOuvertes.length > 0) {
      l.push({ c: 'warn',
        t: pluriel(consignesOuvertes.length, 'consigne non faite', 'consignes non faites'),
        s: 'ELLES PARTENT DANS LE BRIEFING DE DEMAIN' })
    }
    if (run.padAttente.length > 0) {
      l.push({ c: 'warn',
        t: pluriel(run.padAttente.length, 'PAD en attente de justificatif',
                   'PAD en attente de justificatif'),
        s: 'LA FILE POURSUIT LES JOURNÉES SUIVANTES' })
    }
    if (coffreSansMotif) {
      l.push({ c: 'stop', t: `Écart de coffre ${fmtEcart(coffre.ecart)} sans motif`,
        s: 'AUCUNE EXPLICATION ENREGISTRÉE À L’ÉTAPE 10' })
    }
    if (caisses.nb > 0) {
      l.push({ c: lampeEcart(caisses.cumul),
        t: `${pluriel(caisses.nb, 'caisse')} en écart — cumul ${fmtEcart(caisses.cumul)}`,
        s: `PIRE ÉCART ${fmtEcart(caisses.pire).toUpperCase()} · NET ${fmtEcart(caisses.net)}` })
    }
    if (prelev.compte && prelev.ecart !== 0) {
      l.push({ c: lampeUnites(prelev.ecart),
        t: `Prélèvements : ${fmtEcartUnites(prelev.ecart, 'enveloppe')} entre coffre et papier`,
        s: `SOIT ${fmtEcart(prelev.ecartEuros).toUpperCase()}` })
    }
    if (p.forcees > 0) {
      l.push({ c: 'warn',
        t: pluriel(p.forcees, 'étape validée en forçant', 'étapes validées en forçant'),
        s: 'VALIDÉES MALGRÉ LEURS CONTRÔLES · TRACÉES AU JOURNAL' })
    }
    return l
  }, [reste, consignesOuvertes.length, run.padAttente.length, coffreSansMotif,
      coffre.ecart, caisses, prelev, p.forcees])

  const cloturer = async () => {
    if (travail) return
    setTravail('cloture')
    try {
      const stats = await run.cloturer()
      setConfirmation(false)
      toast(`JOURNÉE CLÔTURÉE — ${stats.validees ?? 0} ÉTAPES VALIDÉES`)
    } catch (e) {
      const msg = (e as Error)?.message === 'hors_ligne_cloture'
        ? 'Clôture impossible hors ligne — la confirmation serveur est requise.'
        : messageErreur(e)
      toast(msg, true)
    } finally { setTravail(null) }
  }

  const rouvrir = async () => {
    if (travail) return
    setTravail('reouverture')
    try {
      await run.rouvrirRun()
      setReouverture(false)
      toast('JOURNÉE ROUVERTE — LES ÉTAPES REDEVIENNENT MODIFIABLES')
    } catch (e) {
      const msg = (e as Error)?.message === 'hors_ligne_cloture'
        ? 'Réouverture impossible hors ligne — la confirmation serveur est requise.'
        : messageErreur(e)
      toast(msg, true)
    } finally { setTravail(null) }
  }

  const marquerFaite = async (id: string) => {
    if (travail) return
    setTravail(id)
    try {
      // L'acteur, c'est MOI — pas le tenant du verrou, qui peut être quelqu'un
      // d'autre (ou personne). Et le rechargement vient APRÈS l'écriture : le
      // faire avant coûtait un aller-retour complet pour rien.
      await api.majConsigne(id, 'faite', nom)
      await run.recharger()
    } catch (e) { toast(messageErreur(e), true) }
    finally { setTravail(null) }
  }

  return (
    <div className="wrap" style={{ padding: '18px 0 60px' }}>
      <div className="st-head">
        <h1 className="st-title">
          <small>
            RUN {date} · {cloturee ? `CLÔTURÉE À ${heure(run.run?.cloturee_at)}` : 'EN COURS'}
          </small>
          Bilan de journée
        </h1>
        <div className="tools">
          <button className="btn gh" onClick={() => aller({ vue: 'accueil' })}>← Accueil</button>
          <button className="btn gh" onClick={() => aller({ vue: 'journal', date })}>Journal</button>
          <button className="btn gh" onClick={() => aller({ vue: 'remise', date })}>Remise</button>
          {!cloturee && (
            // L'action la plus lourde de l'application : la plus grande, seule
            // de son ton dans la zone, et jamais sans sa confirmation. Hors
            // ligne elle reste ouvrable — la liste de ce qui reste se lit sans
            // réseau — mais la raison du refus est dite avant le clic, pas au
            // fond de la modale.
            <button className="btn pri lg" onClick={() => setConfirmation(true)}
                    title={run.enLigne ? undefined
                      : 'Hors ligne — la clôture exige la confirmation du serveur'}>
              Clôturer la journée
            </button>
          )}
        </div>
      </div>

      {cloturee && (
        <Notice c="ok" action={
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn sm" disabled={!run.enLigne}
                    title={run.enLigne ? undefined : 'Hors ligne — la réouverture exige la confirmation du serveur'}
                    onClick={() => setReouverture(true)}>
              Rouvrir la journée
            </button>
            <button className="btn sm" onClick={() => aller({ vue: 'accueil' })}>
              Revenir à l’accueil
            </button>
          </div>
        }>
          <b>Journée clôturée</b> à {heure(run.run?.cloturee_at)} — au sens FlashCP et au sens
          SotCP, dans la même transaction. Le bilan reste consultable ; les étapes sont en
          lecture seule.
        </Notice>
      )}

      {!cloturee && !run.enLigne && (
        <Notice c="warn">
          <b>Hors ligne</b> — la clôture exige la confirmation du serveur et reste indisponible.
          {run.attente > 0 && ` ${pluriel(run.attente, 'modification en attente', 'modifications en attente')} `
            + `repartira${run.attente > 1 ? 'ont' : ''} au retour du réseau.`}
        </Notice>
      )}

      {/* 1 · durée et natures */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="bl-hero">
          <div><div className="lbl">DURÉE</div>
            <div className="big2">{duree(run.run?.duree_cumulee_s ?? 0)}</div></div>
          <div><div className="lbl">ÉTAPES VALIDÉES</div>
            <div className="big2">{p.validees}<span style={{ fontSize: 16, color: 'var(--ink4)' }}>/{p.total}</span></div></div>
          <div><div className="lbl">NON APPLICABLES</div>
            <div className="big2" style={{ color: 'var(--ink3)' }}>{p.naRegle + p.naQuestion}</div></div>
          <div><div className="lbl">DE CÔTÉ</div>
            <div className="big2" style={{ color: p.deCote ? 'var(--warn)' : 'var(--ink3)' }}>{p.deCote}</div></div>
          <div><div className="lbl">FORCÉES</div>
            <div className="big2" style={{ color: p.forcees ? 'var(--warn)' : 'var(--ink3)' }}>{p.forcees}</div></div>
          <div className="sp" />
          <div>
            <div className="lbl">JOURNÉE</div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{formatDate(date)}</div>
          </div>
        </div>
      </div>

      {consignesOuvertes.length > 0 && !cloturee && (
        <Card titre="Consignes du jour, avant le bilan" style={{ marginBottom: 14 }}
              lampe={pire(...consignesOuvertes.map<Couleur>((c) => (c.urgence === 3 ? 'stop' : 'warn')))}>
          {consignesOuvertes.map((c) => (
            <div className="li" key={c.id}>
              <Lamp c={c.urgence === 3 ? 'stop' : 'warn'} />
              <div className="body">
                <div className="t">{c.texte}</div>
                <div className="s">DE {(c.auteur_nom ?? '—').toUpperCase()}{c.etape_cible ? ` · ÉTAPE ${c.etape_cible}` : ''}</div>
              </div>
              <button className="btn ok sm" data-encours={travail === c.id ? 'true' : undefined}
                      onClick={() => void marquerFaite(c.id)}>
                Marquer faite
              </button>
            </div>
          ))}
        </Card>
      )}

      <div className="bl-grid">
        {/* 2 · déroulé et attribution */}
        <Card titre="Déroulé et attribution">
          <div className="scrollx">
            <table className="t">
              <thead><tr><th>Étape</th><th>État</th><th>Par</th><th className="n">Heure</th></tr></thead>
              <tbody>
                {[...etapes].sort((a, b) => a.rang - b.rang).map((e) => (
                  <tr key={e.code}>
                    <td><span className="mo" style={{ fontSize: 10, color: 'var(--ink4)' }}>{e.code}</span>{' '}{e.libelle}</td>
                    <td>
                      {/* Une validation FORCÉE n'est pas une conformité : elle a
                          passé outre ses contrôles. Le vert lui était volé. */}
                      {e.statut === 'validee' ? (e.force_valide
                          ? <Chip c="warn">VALIDÉE EN FORÇANT</Chip>
                          : <Chip c="ok">VALIDÉE</Chip>)
                        : e.statut === 'de_cote' ? <Chip c="warn">DE CÔTÉ</Chip>
                        : e.statut === 'skip_regle' ? <Chip>N/A RÈGLE</Chip>
                        : e.statut === 'skip_question' ? <Chip>N/A RÉPONSE</Chip>
                        : <Chip c="stop">NON FAITE</Chip>}
                    </td>
                    <td className="mo" style={{ fontSize: 11 }}>{e.valide_par_nom ?? '—'}</td>
                    <td className="n" style={{ fontSize: 11, color: 'var(--ink3)' }}>{e.valide_at ? heure(e.valide_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div>
          {/* 3 · écarts — le cœur du bilan */}
          <Card titre="Récapitulatif des écarts" lampe={lampeEcarts}
                actions={<Chip>ÉCHELLE · 2 € / 5 €</Chip>} style={{ marginBottom: 14 }}>
            <LigneEcart
              nom="Écarts caisses — cumul"
              connu={caisses.compte > 0}
              valeur={fmtEcart(caisses.cumul)}
              couleur={couleurEcart(caisses.cumul)}
              titre={`Somme des écarts en valeur absolue · ${motEcart(caisses.cumul)} · pire caisse ${fmtEcart(caisses.pire)}`}
              detail={caisses.compte === 0 ? 'rapport opérateur non importé'
                : caisses.nb === 0 ? `${pluriel(caisses.compte, 'caisse')} · aucune en écart`
                : `${pluriel(caisses.nb, 'caisse')} sur ${caisses.compte} · pire ${fmtEcart(caisses.pire)}`}
            />
            {/* Le NET se lit à part : c'est ce que la journée doit expliquer,
                pas ce qui a dérapé. Et un net nul obtenu PAR COMPENSATION n'est
                pas une conformité : il reste neutre, le vert dirait le contraire
                de ce que la ligne du dessus vient d'annoncer. */}
            <LigneEcart
              nom="Écarts caisses — net"
              connu={caisses.compte > 0}
              valeur={fmtEcart(caisses.net)}
              couleur={caisses.nb > 0 && severite(caisses.net) === 'nul'
                ? 'var(--ink3)' : couleurEcart(caisses.net)}
              titre={`Somme signée des écarts · ${motEcart(caisses.net)}`}
              detail={caisses.nb > 0 && severite(caisses.net) === 'nul'
                ? 'nul par compensation — voir le cumul'
                : 'somme signée : deux écarts opposés se compensent'}
            />
            <LigneEcart
              nom="Écart de coffre"
              connu={coffre.existe}
              valeur={fmtEcart(coffre.ecart)}
              couleur={couleurEcart(coffre.ecart)}
              titre={`Compté − attendu · ${motEcart(coffre.ecart)}`}
              detail={coffre.existe ? undefined : 'inventaire non commencé'}
              chip={coffre.existe && severite(coffre.ecart) !== 'nul'
                ? (coffre.motifEcart.trim()
                    ? <Chip>JUSTIFIÉ</Chip>
                    : <Chip c="stop">SANS MOTIF</Chip>)
                : undefined}
            />
            <LigneEcart
              nom="Divergences PAD"
              connu={pad.lignes.length > 0}
              valeur={fmtEcart(pad.ecartTotal)}
              couleur={couleurEcart(pad.ecartTotal)}
              titre={`Montant réellement encaissé − montant ORIKA · ${motEcart(pad.ecartTotal)}`}
              detail={pad.lignes.length === 0 ? 'aucun PAD dans l’import'
                : `${pad.statues} / ${pad.lignes.length} PAD statués`}
            />
            {/* Un comptage d'ENVELOPPES, jamais un montant : une enveloppe vaut
                500 €, et « 9 enveloppes d'écart » n'est pas « 9 € d'écart ». */}
            <LigneEcart
              nom="Prélèvements — coffre / papier"
              connu={prelev.compte}
              valeur={fmtEcartUnites(prelev.ecart, 'enveloppe')}
              couleur={couleurEcartUnites(prelev.ecart)}
              titre="Enveloppes comptées au coffre − enveloppes du papier"
              detail={prelev.compte
                ? (prelev.ecart === 0 ? `${prelev.coffre} de part et d’autre`
                  : `soit ${fmtEcart(prelev.ecartEuros)}`)
                : prelev.attendu > 0
                  ? `${pluriel(prelev.attendu, 'enveloppe')} annoncée${prelev.attendu > 1 ? 's' : ''}, aucune comptée`
                : 'comptage non commencé'}
            />
            {/* Une file de dossiers ouverts n'est pas un écart de mesure :
                trois PAD en attente ne sont pas « critiques », ils sont à
                suivre. Même cotation qu'en réserve de clôture et qu'au
                briefing — le bilan doit dire la même chose partout. */}
            <LigneEcart
              nom="PAD restés en attente"
              connu
              valeur={String(run.padAttente.length)}
              couleur={run.padAttente.length > 0 ? 'var(--warn)' : 'var(--ok)'}
              titre="Dossiers ouverts, toutes journées confondues"
              detail={run.padAttente.length === 0 ? 'aucun dossier ouvert' : 'la file poursuit les journées suivantes'}
            />
          </Card>

          {/* 4 · totaux financiers */}
          <Card titre="Totaux financiers du jour" style={{ marginBottom: 14 }}>
            <div className="slot"><span className="nm">PAD — montant ORIKA</span><span className="sp" />
              <span className="mo">{pad.lignes.length ? fmtEur(pad.total) : '—'}</span></div>
            <div className="slot"><span className="nm">PAD — montant réel encaissé</span><span className="sp" />
              <span className="mo">
                {pad.lignes.length ? fmtEur(+(pad.total + pad.ecartTotal).toFixed(2)) : '—'}
              </span></div>
            <div className="slot">
              <span className="nm">Règlements non-PAD vérifiés</span><span className="sp" />
              {dep.total === 0
                ? <span className="mo" style={{ color: 'var(--ink4)' }}>aucun règlement à vérifier</span>
                : (
                  <span className="mo" style={{ color: couleurEcartUnites(dep.total - dep.verifies) }}
                        title={dep.verifies === dep.total ? 'Tous vérifiés'
                          : `${dep.total - dep.verifies} restant(s) à vérifier`}>
                    {dep.verifies} / {dep.total}
                  </span>
                )}
            </div>
            <div className="slot"><span className="nm">Coffre — compté</span><span className="sp" />
              <span className="mo">{coffre.existe ? fmtEur(coffre.totalCompte) : '—'}</span></div>
            <div className="slot"><span className="nm">Coffre — solde attendu</span><span className="sp" />
              <span className="mo">{coffre.existe ? fmtEur(coffre.soldeAttendu) : '—'}</span></div>
          </Card>

          {/* 6 · tickets */}
          <Card titre="Tickets contrôlés et manquants" lampe={lampeTickets} style={{ marginBottom: 14 }}>
            {(['retours', 'formation', 'annules'] as const).map((k) => {
              const t = tickets[k]
              return (
                <div className="slot" key={k}>
                  <span className="nm">{LIBELLE_TICKETS[k]}</span>
                  <span className="sp" />
                  {t.total === 0
                    ? <span className="mo" style={{ fontSize: 11, color: 'var(--ink4)' }}>aucun ticket importé</span>
                    : (
                      <span className="mo" style={{ fontSize: 11 }}>
                        {/* Le seul vert de la ligne, et il se mérite : « 0 validés »
                            en vert à côté de douze tickets jamais vus disait
                            exactement l'inverse du vrai. Le dénominateur avec,
                            pour n'avoir pas à faire la soustraction de tête. */}
                        <span style={{ color: t.valides === t.total ? 'var(--ok)' : 'var(--ink3)' }}>
                          {t.valides} / {t.total} validés
                        </span>
                        {' · '}
                        <span style={{ color: couleurEcartUnites(t.jamaisVus) }}>
                          {t.jamaisVus} jamais vus
                        </span>
                        {' · '}
                        <span style={{ color: couleurEcartUnites(t.manquants + t.anomalies) }}>
                          {t.manquants + t.anomalies} anomalies
                        </span>
                      </span>
                    )}
                </div>
              )
            })}
          </Card>

          {/* 8 · capture Honeywell */}
          <Card titre="Capture Honeywell" lampe="hw">
            {sessions.length === 0
              ? <Vide>Aucune session de capture — l’armement arrive en phase 3.</Vide>
              : sessions.map((s) => (
                <div className="li" key={s.id}>
                  <Lamp c="hw" />
                  <div className="body">
                    <div className="t">{s.module} — {s.statut}</div>
                    <div className="s">
                      {Array.isArray(s.elements) ? s.elements.length : 0} ÉLÉMENTS ·
                      {' '}OUVERTE À {heure(s.ouverte_at)} PAR {(s.ouverte_par_nom ?? '—').toUpperCase()}
                    </div>
                  </div>
                </div>
              ))}
          </Card>
        </div>
      </div>

      {/* 5 · ce qui part dans le briefing de demain */}
      {/* La lampe prend la PIRE de ses lignes, et cette carte en porte trois
          familles. Elle ne regardait que les étapes : elle restait VERTE au-dessus
          de quatre PAD en attente et de deux consignes non faites — un vert
          au-dessus, en toutes lettres, de ce qui repart demain. */}
      <Card titre="Ce qui partira dans le briefing de demain"
            lampe={pire(
              reste.some((e) => e.statut !== 'de_cote') ? 'stop' : 'ok',
              reste.length > 0 ? 'warn' : 'ok',
              run.padAttente.length > 0 ? 'warn' : 'ok',
              ...consignesOuvertes.map<Couleur>((c) => (c.urgence === 3 ? 'stop' : 'warn')),
            )}
            style={{ marginTop: 14 }}>
        {reste.length === 0 && run.padAttente.length === 0 && consignesOuvertes.length === 0
          ? <Vide>Rien mis de côté, rien en attente — journée soldée.</Vide>
          : (
            <>
              {reste.map((e) => (
                <div className="li" key={e.code}>
                  <Lamp c={e.statut === 'de_cote' ? 'warn' : 'stop'} />
                  <div className="body">
                    <div className="t">{e.code} · {e.libelle} — {e.statut === 'de_cote' ? 'mise de côté' : 'jamais faite'}</div>
                    <div className="s">
                      {e.statut === 'de_cote'
                        ? `MOTIF : ${(e.motif ?? '—').toUpperCase()}`
                        : 'JAMAIS OUVERTE — ELLE REVIENDRA DEMAIN'}
                    </div>
                  </div>
                  {!cloturee && (
                    <button className="btn sm" onClick={() => aller({ vue: 'run', date, etape: e.code })}>
                      Ouvrir l’étape {e.code}
                    </button>
                  )}
                </div>
              ))}
              {run.padAttente.map((p2) => (
                <div className="li" key={p2.id}>
                  <Lamp c="warn" />
                  <div className="body">
                    <div className="t">PAD {p2.pad_ref} — {fmtEur(Number(p2.montant_attendu) || 0)}</div>
                    <div className="s">EN ATTENTE DEPUIS LE {formatMedium(p2.journee_origine).toUpperCase()}</div>
                  </div>
                </div>
              ))}
              {consignesOuvertes.map((c) => (
                <div className="li" key={c.id}>
                  <Lamp c={c.urgence === 3 ? 'stop' : 'warn'} />
                  <div className="body">
                    <div className="t">Consigne — {c.texte}</div>
                    <div className="s">
                      DE {(c.auteur_nom ?? '—').toUpperCase()}
                      {c.etape_cible ? ` · ÉTAPE ${c.etape_cible}` : ''} · NON FAITE
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
      </Card>

      <div style={{ marginTop: 14 }}>
        <Chantier titre="Export PDF et envoi par mail">
          L’export du bilan et son envoi réutiliseront l’infrastructure e-mail V2 des tickets
          support (phase 5). Les signalements caissiers rejoindront ce bilan quand ils seront
          portés dans l’étape 05.
        </Chantier>
      </div>

      {confirmation && (
        <Modale titre="Clôturer la journée ?"
                sous={reserves.length === 0
                  ? 'Rien ne reste ouvert. La journée passera en statut clôturé au sens SotCP, dans la même transaction que la clôture FlashCP.'
                  : 'La clôture reste possible — FlashCP ne décide pas à ta place. Voici ce qu’elle emporte avec elle.'}
                fermer={() => setConfirmation(false)}>
          {reserves.length === 0 ? (
            <Notice c="ok">
              Toutes les étapes sont soldées, aucune consigne ouverte, aucun écart sans motif.
            </Notice>
          ) : (
            <div className="card" style={{ marginBottom: 4 }}>
              {reserves.map((r) => (
                <div className="li" key={r.t}>
                  <Lamp c={r.c} />
                  <div className="body">
                    <div className="t">{r.t}</div>
                    <div className="s">{r.s}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {run.attente > 0 && (
            <Notice c="warn">
              {pluriel(run.attente, 'modification n’est pas encore partie', 'modifications ne sont pas encore parties')}{' '}
              au serveur. Attends leur retour avant de clôturer, sinon elles se poseront sur une
              journée déjà fermée.
            </Notice>
          )}

          <div className="btns">
            <button className="btn" onClick={() => setConfirmation(false)}>Annuler</button>
            <button className="btn pri" disabled={!run.enLigne}
                    data-encours={travail === 'cloture' ? 'true' : undefined}
                    title={run.enLigne ? undefined : 'La clôture exige la confirmation du serveur'}
                    onClick={() => void cloturer()}>
              Clôturer la journée
            </button>
          </div>
        </Modale>
      )}

      {reouverture && (
        <Modale titre="Rouvrir la journée ?"
                sous="La run et la journée SotCP repassent en cours, dans la même transaction — les étapes redeviennent modifiables. Elle recompte parmi les journées ouvertes."
                fermer={() => setReouverture(false)}>
          <Notice c="warn">
            Une journée clôturée est un état arrêté : ne la rouvre que pour corriger. Si trois
            journées sont déjà ouvertes, le serveur refusera — clôtures-en une d’abord.
          </Notice>
          <div className="btns">
            <button className="btn" onClick={() => setReouverture(false)}>Annuler</button>
            <button className="btn pri" disabled={!run.enLigne}
                    data-encours={travail === 'reouverture' ? 'true' : undefined}
                    title={run.enLigne ? undefined : 'La réouverture exige la confirmation du serveur'}
                    onClick={() => void rouvrir()}>
              Rouvrir la journée
            </button>
          </div>
        </Modale>
      )}
    </div>
  )
}

/**
 * Une ligne d'écart : le nom, ce qu'on en sait, le chiffre SIGNÉ et sa couleur.
 *
 * `connu` sépare « il n'y a pas d'écart » de « on ne l'a pas encore mesuré ».
 * Les confondre affichait un tiret vert, ou pire, un zéro vert rassurant sur un
 * contrôle qui n'avait pas eu lieu.
 */
function LigneEcart({ nom, connu, valeur, couleur, titre, detail, chip }: {
  nom: string
  connu: boolean
  valeur: string
  couleur: string
  titre?: string
  detail?: string
  chip?: ReactNode
}) {
  return (
    <div className="slot">
      <span className="nm">{nom}</span>
      {detail && (
        <span style={{ fontSize: 10.5, color: 'var(--ink3)' }}>{detail}</span>
      )}
      <span className="sp" />
      {chip}
      <span className="mo" style={{ color: connu ? couleur : 'var(--ink4)' }}
            title={connu ? titre : 'Contrôle non effectué'}>
        {connu ? valeur : '—'}
      </span>
    </div>
  )
}
